import usePlacesAutocomplete, {
    getGeocode,
} from 'use-places-autocomplete';
import { useState } from 'react';
import toast from 'react-hot-toast';

interface AddressData {
    addressLine1: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
}

interface AddressAutocompleteProps {
    onAddressSelect: (data: AddressData) => void;
    defaultValue?: string;
}

// Nova Scotia geographic bounds
const NS_BOUNDS = {
    north: 47.0,
    south: 43.3,
    west: -66.5,
    east: -59.5,
} as const;

export function AddressAutocomplete({ onAddressSelect, defaultValue = '' }: AddressAutocompleteProps) {
    const [open, setOpen] = useState(false);

    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {
            componentRestrictions: { country: 'ca' },
            locationRestriction: {
                north: NS_BOUNDS.north,
                south: NS_BOUNDS.south,
                east: NS_BOUNDS.east,
                west: NS_BOUNDS.west,
            },
        },
        debounce: 300,
        defaultValue,
    });

    const handleSelect = async (address: string) => {
        setValue(address, false);
        clearSuggestions();
        setOpen(false);

        try {
            const results = await getGeocode({ address });
            const result = results[0];

            // Parse address components
            let addressLine1 = '';
            let streetNumber = '';
            let route = '';
            let city = '';
            let province = '';
            let postalCode = '';
            let country = '';

            console.log('Google Maps Result:', result);

            result.address_components.forEach((component) => {
                const types = component.types;

                if (types.includes('street_number')) {
                    streetNumber = component.long_name;
                }
                if (types.includes('route')) {
                    route = component.long_name;
                }
                if (types.includes('locality')) {
                    city = component.long_name;
                }
                if (types.includes('administrative_area_level_1')) {
                    province = component.short_name; // Use short code (e.g., NS, ON)
                }
                if (types.includes('postal_code')) {
                    postalCode = component.long_name;
                }
                if (types.includes('country')) {
                    country = component.long_name;
                }
            });

            // Validate that the address is within Nova Scotia
            const isNovaScotia = result.address_components.some(
                (component) =>
                    component.types.includes('administrative_area_level_1') &&
                    (component.short_name === 'NS' || component.long_name === 'Nova Scotia')
            );

            if (!isNovaScotia) {
                toast.error('Please select an address within Nova Scotia.');
                setValue('', false);
                return;
            }

            addressLine1 = `${streetNumber} ${route}`.trim();
            // Fallback if no specific street number/route (e.g. searching for a building name)
            if (!addressLine1) {
                addressLine1 = address.split(',')[0];
            }

            onAddressSelect({
                addressLine1,
                city,
                province,
                postalCode,
                country
            });

        } catch (error) {
            console.error('Error: ', error);
        }
    };

    return (
        <div className="relative w-full">
            {/* We use a simple input with a dropdown for now, or use the Command component pattern if available */}
            <div className="relative">
                <input
                    disabled={!ready}
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setOpen(true);
                    }}
                    placeholder="Search address..."
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                {status === 'OK' && open && (
                    <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
                        {data.map(({ place_id, description }) => (
                            <li
                                key={place_id}
                                onClick={() => handleSelect(description)}
                                className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer"
                            >
                                {description}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
